import {req} from '../Utils/ApiUtil';
import {_convertDocument, _convertQuery} from './JStorageCompat';
import Config from '../../data.json';
import {getOutlet} from 'reconnect.js';

async function fetchDocuments(
  collection,
  query = {},
  sorting = [],
  paging = {offset: 0, limit: 100},
  projection = null,
  options = {anonymous: false, token: ''},
) {
  const resp = await req(
    _appendToken(
      `${Config.jstoreHost}/document/${collection}/find`,
      options.anonymous,
      options.token,
    ),
    {
      method: 'POST',
      data: {
        query: _convertQuery(query),
        // make sure we always sort the records with desc _id
        sorting: !sorting ? ['-_id'] : [...sorting, '-_id'],
        ...(paging ? {paging} : {}),
        ...(projection ? {projection} : {}),
      },
    },
  );

  if (resp.results) {
    return {
      ...resp,
      results: resp.results.map(_convertDocument),
    };
  } else {
    return resp.map(_convertDocument);
  }
}

async function fetchAllDocuments(
  collection,
  query = {},
  sorting = [],
  projection = null,
  options = {anonymous: false, token: ''},
) {
  const resp = await req(
    _appendToken(
      `${Config.jstoreHost}/document/${collection}/find`,
      options.anonymous,
      options.token,
    ),
    {
      method: 'POST',
      data: {
        query: _convertQuery(query),
        // make sure we always sort the records with desc _id
        sorting: !sorting ? ['-_id'] : [...sorting, '-_id'],
        ...(projection ? {projection} : {}),
      },
    },
  );

  return resp.map(_convertDocument);
}

async function cacheDocuments(
  collection,
  query = {},
  sorting = [],
  paging = {offset: 0, limit: 100},
  projection = null,
  options = {anonymous: false, token: ''},
  cacheOptions,
) {
  return await req(
    _appendToken(
      `${Config.jstoreHost}/document/${collection}/cache`,
      options.anonymous,
      options.token,
    ),
    {
      method: 'POST',
      data: {
        query: _convertQuery(query),
        // make sure we always sort the records with desc _id
        sorting: !sorting ? ['-_id'] : ['-_id', ...sorting],
        ...(paging ? {paging} : {}),
        ...(projection ? {projection} : {}),
        key: cacheOptions.key,
        acl: cacheOptions.acl || 'public-read',
      },
    },
  );
}

async function fetchOneDocument(
  collection,
  query = {},
  projection = null,
  options = {anonymous: false, token: ''},
) {
  const resp = await req(
    _appendToken(
      `${Config.jstoreHost}/document/${collection}/find-one`,
      options.anonymous,
      options.token,
    ),
    {
      method: 'POST',
      data: {
        query: _convertQuery(query),
        ...(projection ? {projection} : {}),
      },
    },
  );

  return _convertDocument(resp);
}

async function createDocument(
  collection,
  data,
  options = {anonymous: false, token: ''},
) {
  const nextDoc = await req(
    _appendToken(
      `${Config.jstoreHost}/document/${collection}/create`,
      options.anonymous,
      options.token,
    ),
    {
      method: 'POST',
      data: {data},
    },
  );
  return _convertDocument(nextDoc);
}

async function updateDocument(
  collection,
  query,
  data,
  options = {anonymous: false, token: ''},
) {
  const {anonymous, token, ...restOptions} = options;

  const nextDoc = await req(
    _appendToken(
      `${Config.jstoreHost}/document/${collection}/update`,
      anonymous,
      token,
    ),
    {
      method: 'POST',
      data: {
        query: _convertQuery(query),
        data,
        option: {...restOptions},
      },
    },
  );
  return _convertDocument(nextDoc);
}

async function deleteDocument(
  collection,
  query,
  options = {anonymous: false, token: ''},
) {
  return await req(
    _appendToken(
      `${Config.jstoreHost}/document/${collection}/find-one/delete`,
      options.anonymous,
      options.token,
    ),
    {
      method: 'POST',
      data: {query: _convertQuery(query)},
    },
  );
}

async function bulkWriteDocuments(
  collection,
  operations,
  options = {anonymous: false, token: ''},
) {
  return await req(
    _appendToken(
      `${Config.jstoreHost}/document/${collection}/bulk-write`,
      options.anonymous,
      options.token,
    ),
    {
      method: 'POST',
      data: {actions: operations},
    },
  );
}

async function aggregateDocuments(
  collection,
  operations,
  options = {anonymous: false, token: ''},
) {
  return await req(
    _appendToken(
      `${Config.jstoreHost}/document/${collection}/aggregate`,
      options.anonymous,
      options.token,
    ),
    {
      method: 'POST',
      data: {
        data: operations,
      },
    },
  );
}

/**
 * **************************************************
 * Helpers
 * **************************************************
 */

function _appendToken(url, anonymous, token) {
  if (!token) {
    token = getOutlet('user').getValue()?.token;
    // it also might be undefined, by anonymous = true
  }

  let separator = '?';

  // TODO: find better way for this
  if (url.indexOf('?') > -1) {
    separator = '&';
  }

  if (token && token.indexOf('tmp_') !== 0 && !anonymous) {
    return url + `${separator}token=${token}`;
  } else {
    return url + `${separator}client_id=${Config.clientId}`;
  }
}

export {
  fetchDocuments,
  fetchOneDocument,
  fetchAllDocuments,
  createDocument,
  updateDocument,
  deleteDocument,
  bulkWriteDocuments,
  cacheDocuments,
  aggregateDocuments,
};
